import React from 'react';
import { Box, Skeleton, Typography, useTheme } from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';

interface DeliveryItem {
  id: string;
  name: string;
  value: number;
}

interface DeliveriesBarChartProps {
  data: DeliveryItem[];
  title: string;
  loading: boolean;
  granularityNote?: string;
}

const numberFormatter = new Intl.NumberFormat('pt-BR');

const DeliveriesBarChart: React.FC<DeliveriesBarChartProps> = ({
  data,
  title,
  loading,
  granularityNote,
}) => {
  const theme = useTheme();

  if (loading) {
    return <Skeleton variant="rectangular" width="100%" height={220} sx={{ borderRadius: 1 }} />;
  }

  // Filtrar zeros e ordenar desc
  const filtered = (data ?? [])
    .filter((item) => item.value > 0)
    .sort((a, b) => b.value - a.value);

  if (filtered.length === 0) {
    return (
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          {title}
        </Typography>
        <Box
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 220 }}
        >
          <Typography variant="body1" color="text.secondary">
            Sem dados de entregas
          </Typography>
        </Box>
        {granularityNote && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            * {granularityNote}
          </Typography>
        )}
      </Box>
    );
  }

  const chartHeight = Math.max(220, filtered.length * 36);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      return (
        <Box
          sx={{
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            p: 1.5,
            maxWidth: 260,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
            {item.name}
          </Typography>
          <Typography variant="body2">
            {numberFormatter.format(item.value)} entregas
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        {title}
      </Typography>
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart
          layout="vertical"
          data={filtered}
          margin={{ top: 4, right: 70, left: 8, bottom: 4 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} horizontal={false} />
          <XAxis
            type="number"
            tick={{ fontSize: 11 }}
            stroke={theme.palette.text.secondary}
            tickFormatter={(v: number) => numberFormatter.format(v)}
          />
          <YAxis
            type="category"
            dataKey="name"
            tick={{ fontSize: 11 }}
            stroke={theme.palette.text.secondary}
            width={140}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar
            dataKey="value"
            fill={theme.palette.primary.main}
            radius={[0, 4, 4, 0]}
            label={{
              position: 'right',
              formatter: (val: any) => numberFormatter.format(Number(val)),
              fontSize: 11,
            }}
          />
        </BarChart>
      </ResponsiveContainer>
      {granularityNote && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          * {granularityNote}
        </Typography>
      )}
    </Box>
  );
};

export default DeliveriesBarChart;
